import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

/**
 * LegalPage — shared layout for Privacy Policy + Terms & Conditions.
 *
 * Public route (no auth). Must remain reachable from the open internet
 * so the LinkedIn Developer Portal (and other OAuth providers' app
 * review processes) can fetch the URLs.
 *
 * The visual language matches the rest of the app — paper-card,
 * font-heading, label-typed, classified stamp — but the copy is plain
 * and operationally accurate. No persona breaks, just an honest
 * account of how the Shed handles your data.
 */
export default function LegalPage({ title, lastUpdated, summary, sections }) {
    return (
        <div className="min-h-screen bg-paper" data-testid={`legal-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <header className="border-b border-ink/20 bg-paper-light/50">
                <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-xs font-body text-ink-soft hover:text-stamp-red uppercase tracking-[0.18em]"
                        data-testid="legal-back-link"
                    >
                        <ArrowLeft className="w-3 h-3" />
                        Back to The Shed
                    </Link>
                    <div className="font-heading uppercase tracking-[0.22em] text-[10px] text-ink-soft flex items-center gap-2">
                        <ShieldCheck className="w-3 h-3" />
                        Public document
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-12">
                <div className="mb-8 relative">
                    <div className="absolute -top-2 right-0 classified-stamp">PUBLISHED</div>
                    <div className="label-typed tw-prefix">Legal · plain language</div>
                    <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-[0.04em] text-ink mt-2">
                        {title}
                    </h1>
                    <div className="font-body text-xs text-ink-soft mt-2">
                        Last updated: {lastUpdated}
                    </div>
                </div>

                <p className="font-body text-base text-ink leading-relaxed mb-10 pl-4 border-l-2 border-stamp-red">
                    {summary}
                </p>

                <div className="space-y-8">
                    {sections.map((section, i) => (
                        <section key={section.heading}>
                            <h2 className="font-heading uppercase tracking-[0.18em] text-lg text-stamp-red mb-3">
                                {i + 1}. {section.heading}
                            </h2>
                            <div className="space-y-3 font-body text-sm text-ink leading-relaxed">
                                {section.body.map((p, j) => (
                                    <p key={j}>{p}</p>
                                ))}
                                {section.bullets && (
                                    <ul className="list-disc ml-6 space-y-1">
                                        {section.bullets.map((b) => (
                                            <li key={b}>{b}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </section>
                    ))}
                </div>

                <footer className="mt-16 pt-8 border-t border-ink/15 font-body text-xs text-ink-soft">
                    <div className="flex flex-wrap gap-4">
                        <Link to="/privacy" className="hover:text-stamp-red">Privacy Policy</Link>
                        <Link to="/terms" className="hover:text-stamp-red">Terms &amp; Conditions</Link>
                        <Link to="/login" className="hover:text-stamp-red ml-auto">Sign in</Link>
                    </div>
                    <div className="mt-3 text-[10px] uppercase tracking-[0.18em]">
                        Spy Finance · Gary's Shed
                    </div>
                </footer>
            </main>
        </div>
    );
}
